/** @jsx dom */
import deepEqual from 'deep-equal';
import dom from 'magic-virtual-element';
import {utils as cmsUtils} from '@viskan/cms-components';
import Slide from '@viskan/deku-slide';
import {Image} from '~/media';
import getIcon from '../../utils/actions';
import ListArticle from '../../../list-article';
import {sanitizeAttr1IdKeyValue} from '~/utils';

const categorySourceArray = ['category', 'Category'];
const recentSourceArray = ['recent', 'Recent Views'];
const selectionSourceArray = ['selection', 'Selection'];

const defaultProps = {
	iconNext: '',
	iconPrev: '',
	imageNext: '',
	imagePrev: ''
};

const stringToArray = str => str ? str.replace(/\s/g, '').split(',') : [];
const parseNumber = (num, def) => parseInt(num, 10) || def;
const getImage = (stateString, props) => <Image class={props.styleClasses.Image} source={props[`image${stateString}`]} data-style-attr='Image'/>;
const getPresentation = (stateString, props) => {
	switch (props.type) {
		case 'icon': {
			return getIcon({icon: props[`icon${stateString}`], iconColor: props[`icon${stateString}Color`], iconSize: props[`icon${stateString}Size`], label: stateString});
		}
		case 'image': {
			return getImage(stateString, props);
		}
		case 'text':
		default: {
			return stateString;
		}
	}
};

const sortArticles = (articles, {articleHistory, articleNumbers, source}) => {
	if (articles.length === 0 || (!selectionSourceArray.includes(source) && !recentSourceArray.includes(source))) {
		return articles;
	}

	if (selectionSourceArray.includes(source)) {
		const sortArr = stringToArray(articleNumbers);
		return sortArr.reduce((acc, curr) => [...acc, articles.find(x => x.art_no === curr)], []).filter(x => x);
	}

	return articleHistory.reduce((acc, curr) => [...acc, articles.find(x => x.art_no === curr.art_no)], []).filter(x => x);
};

const sortArticlesForFetch = articleHistory => {
	const arr = articleHistory.map(({attr1_id, ...keepOnlyArtNo}) => keepOnlyArtNo);
	return arr.slice().sort((a, b) => (a.art_no > b.art_no) ? 1 : ((b.art_no > a.art_no) ? -1 : 0));
};

const fetchArticles = props => {
	const {articlesFetch, articleHistory, articleNumbers, categoryId, id, source, size} = props;

	if (selectionSourceArray.includes(source)) {
		articlesFetch(stringToArray(articleNumbers), {size, key: 'search', id, artNos: true});
		return;
	}

	if (recentSourceArray.includes(source) && articleHistory && articleHistory.length > 0) {
		articlesFetch(articleHistory, {size, key: 'search', id, artNos: true});
		return;
	}

	if (categoryId) {
		articlesFetch(categoryId, {size, key: 'cat_id', id});
	}
};

const getArticles = (articles, props) => articles.map(x => {
	const {articleHistory, image, imageOnHover, source} = props;
	const historyArt = articleHistory.find(history => history.art_no === x.art_no);
	const attr1_id = source === 'recent' ? sanitizeAttr1IdKeyValue(historyArt) : sanitizeAttr1IdKeyValue(x);

	const badgeOptions = {
		campaignPopup: props.campaignPopup,
		campaignHeader: props.campaignHeader,
		showCampaigns: props.showCampaigns,
		showSaleOrNew: props.showSaleOrNew
	};

	const moreInfo = {
		pricePrefix: props.pricePrefix,
		priceSuffix: props.priceSuffix,
		showMoreInfo: props.showMoreInfo,
		showMoreInfoText: props.showMoreInfoText,
		showShortDescription: props.showShortDescription
	};

	return (
		<div key={`${x.art_id}-${x.articleAttributes[0].attr1_id}-${x.articleAttributes[0].attr2_id}`}>
			<ListArticle article={x} attr1_id={attr1_id} badgeOptions={badgeOptions} back={imageOnHover} front={image} moreInfo={moreInfo} styleClasses={props.styleClasses}/>
		</div>
	);
});

const afterMount = ({props}) => fetchArticles(props);

const afterUpdate = ({props}, prevProps) => {
	if (props.source !== prevProps.source ||
		props.size !== prevProps.size ||
		props.clubId !== prevProps.clubId ||
		(recentSourceArray.includes(props.source) && !deepEqual(sortArticlesForFetch(props.articleHistory), sortArticlesForFetch(prevProps.articleHistory))) ||
		(selectionSourceArray.includes(props.source) && props.articleNumbers !== prevProps.articleNumbers) ||
		(categorySourceArray.includes(props.source) && props.categoryId !== prevProps.categoryId)) {
		fetchArticles(props);
	}
};

const shouldUpdate = ({props}, nextProps) => !deepEqual(props, nextProps);

const render = ({props}) => {
	const articles = props.components[props.id] ? props.components[props.id].articles : [];
	const items = getArticles(sortArticles(articles, props), props);
	const iconNext = (props.iconNext || props.imageNext) && props.type !== 'none' ? getPresentation('Next', props) : '';
	const iconPrev = (props.iconPrev || props.imagePrev) && props.type !== 'none' ? getPresentation('Prev', props) : '';
	const key = new Date().getTime();

	const options = {
		roundLengths: true,
		simulateTouch: props.cmsLoaded
	};

	switch (props.breakpoint) {
		case 'xsm':
		case 'sm':
			options.centeredSlides = props.centeredSlidesMobile;
			options.slidesPerColumn = parseNumber(props.slidesPerColumnMobile, 1);
			options.slidesPerGroup = parseNumber(props.slidesPerGroupMobile, 1);
			options.slidesPerView = parseNumber(props.slidesPerViewMobile, 1);
			options.spaceBetween = parseNumber(props.spaceBetweenMobile, 0);
			break;
		case 'md':
			options.centeredSlides = props.centeredSlidesTablet;
			options.slidesPerColumn = parseNumber(props.slidesPerColumnTablet, 1);
			options.slidesPerGroup = parseNumber(props.slidesPerGroupTablet, 1);
			options.slidesPerView = parseNumber(props.slidesPerViewTablet, 1);
			options.spaceBetween = parseNumber(props.spaceBetweenTablet, 0);
			break;
		default:
			options.centeredSlides = props.centeredSlidesDesktop;
			options.slidesPerColumn = parseNumber(props.slidesPerColumnDesktop, 1);
			options.slidesPerGroup = parseNumber(props.slidesPerGroupDesktop, 1);
			options.slidesPerView = parseNumber(props.slidesPerViewDesktop, 1);
			options.spaceBetween = parseNumber(props.spaceBetweenDesktop, 0);
	}

	const settings = {
		arrows: props.arrows,
		iconNext,
		iconPrev,
		direction: 'horizontal',
		disableOnInteraction: true,
		duration: parseNumber(props.duration, 3000),
		effect: props.effect,
		loop: props.loop,
		options,
		pagination: props.pagination,
		paginationType: props.paginationType,
		play: props.play,
		speed: parseNumber(props.speed, 500),
		timing: props.timing
	};

	return cmsUtils.createCmsComponent((
		<div class={['CMS-ArticlesSlider', props.styleClasses.Wrapper]} data-style-attr='Wrapper'>
			<Slide key={key} {...settings} styleClasses={props.styleClasses}>{items}</Slide>
		</div>
	), props);
};

export default {afterMount, afterUpdate, defaultProps, render, shouldUpdate};
